import { hasToken, request, setToken } from 'utils/api';
import { APP_URL } from 'utils/env';
import { currentProjectSlug, projectUrl } from 'utils/projectHost';

// Arriving at a project's own address with a session.
//
// The JWT lives in localStorage, which browsers scope PER ORIGIN — so a
// person signed in on console.hauser.md lands on specs.hauser.md as a
// stranger. This is not hypothetical: gateways/routes.yml carries the
// postmortem of the same mechanism costing a paying customer their
// session between the apex and the console.
//
// The fix is a deliberate, single-use handover rather than a cookie on
// .hauser.md. A parent-domain cookie would be readable by every
// subdomain — including the wildcard hosts where we intend to render
// untrusted tenant HTML — so per-origin sessions are the property worth
// keeping, even at the cost of one redirect the first time.
//
//   project host, no session  →  console /handoff?slug=…&to=…
//   console (has session)     →  POST /1/auth/handoff  →  back with #t=
//   project host              →  POST /1/auth/handoff/redeem  →  session
//
// The token rides in the FRAGMENT, which is never sent to a server and
// never lands in a proxy log, and is stripped from the URL the moment
// it is spent.

export function readHandoffToken(location = window.location) {
  const match = /[#&]t=([^&]+)/.exec(location.hash || '');
  return match ? decodeURIComponent(match[1]) : null;
}

function stripHandoffToken(location = window.location, history = window.history) {
  const hash = (location.hash || '').replace(/[#&]t=[^&]*/, '');
  history.replaceState(
    null,
    '',
    `${location.pathname}${location.search}${hash === '#' ? '' : hash}`,
  );
}

// On a project host: redeem an arriving token, or ask the console for
// one. Returns true when the caller should wait (a redirect is under
// way or a session is being installed).
// Paths a STRANGER can legitimately be on. Share links live on the
// project's own address now (round 8), so bouncing an anonymous
// recipient to the console to fetch a session would send them to a
// login screen for a page that never needed one.
function isPublicPath(pathname) {
  return /^\/s\//.test(String(pathname || ''));
}

export async function ensureProjectHostSession() {
  const slug = currentProjectSlug();
  if (!slug) {
    return false;
  }
  if (isPublicPath(window.location.pathname)) {
    return false;
  }

  const arriving = readHandoffToken();
  if (arriving) {
    try {
      const { data } = await request({
        method: 'POST',
        path: '/1/auth/handoff/redeem',
        body: { token: arriving },
      });
      setToken(data.token);
    } catch {
      // A spent or expired handoff is not an error worth a screen: the
      // app falls through to its normal signed-out path below.
    }
    stripHandoffToken();
    return false;
  }

  if (hasToken()) {
    return false;
  }

  // No session here, and no token in hand: ask the console for one.
  const to = `${window.location.pathname}${window.location.search}`;
  const url = new URL('/handoff', APP_URL);
  url.searchParams.set('slug', slug);
  url.searchParams.set('to', to);
  window.location.replace(url.toString());
  return true;
}

// The fast path: the console has the session, so it mints BEFORE
// letting go of the tab and the project host arrives holding one.
//
// Without this, going to a project is four navigations and three app
// boots — the project host loads, discovers it has no session, goes to
// the console to ask for one, and comes back. The console knew all
// along.
//
// Never blocks the trip: if the mint fails, return the plain address
// and let the bounce below do its job.
export async function projectVisitUrl(slug, path = '/') {
  const target = projectUrl(slug, path);
  if (!hasToken()) {
    return target;
  }
  try {
    const { data } = await request({
      method: 'POST',
      path: '/1/auth/handoff',
      body: { slug },
    });
    return `${target}#t=${encodeURIComponent(data.token)}`;
  } catch {
    return target;
  }
}

// On the console: mint for the requested project and bounce back.
export async function completeHandoff(params) {
  const slug = params.get('slug');
  const to = params.get('to') || '/';
  if (!slug) {
    window.location.replace('/');
    return;
  }
  const { data } = await request({
    method: 'POST',
    path: '/1/auth/handoff',
    body: { slug },
  });
  // The path comes from OUR query string and goes back to a URL built
  // from the slug — the origin is never taken from the request on
  // either side (lib/handoff.js derives it too, and refuses a mismatch).
  const safePath = to.startsWith('/') ? to : '/';
  window.location.replace(`${projectUrl(slug, safePath)}#t=${encodeURIComponent(data.token)}`);
}
